import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { ArrowLeftIcon, ArrowRightIcon, ArrowPathIcon } from '@heroicons/react/24/solid';
import styled from 'styled-components';

const Iframe = styled.iframe`
  width: 100%;
  height: 100%;
  border: 0;
`;

export default function BrowserView() {
  const dispatch = useDispatch();
  const { channelConfig, currentChannel, browserUrl } = useSelector((state) => state.channel);
  const channelInfo = channelConfig[currentChannel] || {};
  const { url: initialUrl } = channelInfo;

  const iframeRef = useRef(null);
  const previousChannel = useRef(currentChannel);
  const [currentUrl, setCurrentUrl] = useState(initialUrl || 'about:blank');
  const [inputUrl, setInputUrl] = useState(initialUrl || 'about:blank'); // Separate state for input
  const [history, setHistory] = useState([initialUrl || 'about:blank']);
  const [historyIndex, setHistoryIndex] = useState(0);
  const [isNavigating, setIsNavigating] = useState(false);

  const navigate = useCallback((url, isNavigatingHistory = false) => {
    if (!url || url === currentUrl || isNavigating) return;
    
    console.log(`BrowserView: Navigate called with URL: ${url}, isNavigatingHistory: ${isNavigatingHistory}`);
    
    setIsNavigating(true);
    
    if (iframeRef.current) {
      iframeRef.current.src = url;
    }
    
    setCurrentUrl(url);
    setInputUrl(url); // Keep input in sync with actual URL
    
    if (!isNavigatingHistory) {
      setHistory(prev => {
        const newHistory = prev.slice(0, historyIndex + 1);
        newHistory.push(url);
        setHistoryIndex(newHistory.length - 1);
        return newHistory;
      });
    }
    
    // Reset navigation flag after a short delay
    setTimeout(() => setIsNavigating(false), 100);
  }, [currentUrl, historyIndex, isNavigating]);

  // Handle external browserUrl changes (from email links, etc.)
  useEffect(() => {
    if (browserUrl && browserUrl !== currentUrl) {
      console.log(`BrowserView: Navigating to external URL: ${browserUrl}`);
      navigate(browserUrl);
      // Clear the browserUrl to prevent repeated navigation
      dispatch({ type: 'channel/setBrowserUrl', payload: null });
    }
  }, [browserUrl, currentUrl, navigate, dispatch]);

  // Handle initial URL load only when channel changes, not on every config update
  useEffect(() => {
    // Only reset to initial URL when the channel actually changes
    if (previousChannel.current !== currentChannel) {
      const newUrl = initialUrl || 'about:blank';
      console.log(`BrowserView: Channel changed from ${previousChannel.current} to ${currentChannel}, resetting to URL: ${newUrl}`);
      setCurrentUrl(newUrl);
      setInputUrl(newUrl); // Keep input in sync
      setHistory([newUrl]);
      setHistoryIndex(0);
      
      if (iframeRef.current) {
        iframeRef.current.src = newUrl;
      }
      
      previousChannel.current = currentChannel;
    }
  }, [initialUrl, currentChannel]);

  const canGoBack = historyIndex > 0;
  const canGoForward = historyIndex < history.length - 1;

  const handleIframeLoad = useCallback(() => {
    if (isNavigating) return; // Skip if we're in the middle of a navigation
    
    try {
      const iframeLocation = iframeRef.current?.contentWindow?.location?.href;
      if (iframeLocation && 
          iframeLocation !== 'about:blank' && 
          iframeLocation !== currentUrl &&
          !iframeLocation.includes('about:srcdoc')) {
        console.log(`BrowserView: Iframe loaded new location: ${iframeLocation}`);
        setCurrentUrl(iframeLocation);
        setInputUrl(iframeLocation); // Keep input in sync
        
        // Only update history if this isn't a programmatic navigation
        setHistory(prev => {
          const lastUrl = prev[prev.length - 1];
          if (lastUrl !== iframeLocation) {
            const newHistory = prev.slice(0, historyIndex + 1);
            newHistory.push(iframeLocation);
            setHistoryIndex(newHistory.length - 1);
            return newHistory;
          }
          return prev;
        });
      }
    } catch (error) {
      // This is expected for cross-origin iframes
      console.debug("Could not access iframe location due to cross-origin restrictions.");
    }
  }, [currentUrl, historyIndex, isNavigating]);

  const handleBack = () => {
    if (canGoBack) {
      const newIndex = historyIndex - 1;
      setHistoryIndex(newIndex);
      const newUrl = history[newIndex];
      navigate(newUrl, true);
    }
  };

  const handleForward = () => {
    if (canGoForward) {
      const newIndex = historyIndex + 1;
      setHistoryIndex(newIndex);
      const newUrl = history[newIndex];
      navigate(newUrl, true);
    }
  };

  const handleRefresh = () => {
    if (iframeRef.current) {
      const currentSrc = iframeRef.current.src;
      iframeRef.current.src = currentSrc;
    }
  };

  const handleUrlSubmit = (e) => {
    if (e) e.preventDefault();
    let newUrl = inputUrl.trim();
    if (newUrl && !/^(https?:\/\/|file:)/i.test(newUrl)) {
      newUrl = 'https://' + newUrl;
    }
    navigate(newUrl);
  };

  const handleUrlBlur = () => {
    // Only navigate if the input URL is different from the current URL
    if (inputUrl.trim() !== currentUrl) {
      handleUrlSubmit();
    }
  };

  return (
    <div className="flex flex-col h-full bg-gray-100">
      <div className="flex items-center p-2 bg-gray-200 border-b border-gray-300 shrink-0">
        <div className="flex space-x-1">
          <button onClick={handleBack} disabled={!canGoBack} className="p-2 rounded-full disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-300">
            <ArrowLeftIcon className="w-5 h-5 text-gray-700" />
          </button>
          <button onClick={handleForward} disabled={!canGoForward} className="p-2 rounded-full disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-300">
            <ArrowRightIcon className="w-5 h-5 text-gray-700" />
          </button>
          <button onClick={handleRefresh} className="p-2 rounded-full hover:bg-gray-300">
            <ArrowPathIcon className="w-5 h-5 text-gray-700" />
          </button>
        </div>
        <div className="flex-grow mx-2">
          <form onSubmit={handleUrlSubmit}>
            <input
              type="text"
              value={inputUrl}
              onChange={(e) => setInputUrl(e.target.value)}
              onBlur={handleUrlBlur}
              className="w-full px-3 py-1 text-sm bg-white border border-gray-300 rounded-full shadow-inner focus:outline-none focus:ring-2 focus:ring-blue-500"
              aria-label="Address bar"
            />
          </form>
        </div>
      </div>
      <div className="relative flex-grow bg-white">
        <Iframe
          ref={iframeRef}
          src={currentUrl}
          onLoad={handleIframeLoad}
          title={channelInfo.name}
          sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-top-navigation allow-top-navigation-by-user-activation"
        />
      </div>
    </div>
  );
};
